import ClearIcon from '@mui/icons-material/Clear';
import { IconButton, TextField, type TextFieldProps } from '@mui/material';
import { useEffect, useRef, useState, type ReactNode, type Ref } from 'react';

export interface DateFieldProps
  extends Omit<TextFieldProps, 'type' | 'value' | 'onChange' | 'error' | 'helperText' | 'inputRef'> {
  label?: ReactNode;
  value: string;
  onChange: (value: string) => void;
  /** Forwards the underlying native <input> DOM node — needed by callers that must reach it
   *  directly (e.g. to call .showPicker() from a paired calendar-icon button, as
   *  ProjectPlannerTab.tsx's DateEntryField does). DateField still keeps its own internal ref
   *  for the incomplete-input tracking below; this one is purely a pass-through. */
  inputRef?: Ref<HTMLInputElement>;
  /** Shown when the browser reports the current value as unparseable/incomplete (e.g. a
   *  month+year typed with no day) — distinct from a field that's simply empty, which a
   *  disabled Save/Add button already handles with no message, same as every other required
   *  field in the app. */
  invalidMessage?: string;
  /** Fires whenever this field's own incomplete-input state changes — fold this into the
   *  dialog's isFormValid so Save/Add can't be clicked while a partially-typed date is still
   *  showing, the same way ProjectKeyField's onValidityChange is used. */
  onValidityChange?: (isValid: boolean) => void;
  /** Caller-computed bounds/order error — applies once a complete value is present. */
  error?: boolean;
  helperText?: ReactNode;
  /** Turns off the incomplete-input safety net entirely — no error, no message, onValidityChange
   *  never fires — so the field behaves exactly like a plain TextField. Use this to adopt
   *  DateField purely for component consistency on a field that must keep its exact prior
   *  behavior (e.g. a required field where the app's convention is "no message, ever" — only a
   *  disabled Save/Add button). */
  disableIncompleteCheck?: boolean;
}

// A native date input's year segment doesn't report validity.badInput while it's still being
// typed digit-by-digit into a blank/cleared segment — verified against a real browser, Chromium
// zero-pads whatever's been typed so far into a "complete" 4-digit year immediately ("2" -> year
// 0002, badInput false), not just once four digits are entered. A year before 2000 is never a
// legitimate value for anything this app tracks (project/staffing/task dates), so it's a reliable
// signal that what's showing is still a mid-typing padding artifact, not a value the user meant.
// The HTML5 date spec also allows a year segment of *more* than 4 digits (Chromium's year
// spinner accepts up to 6), so a year is only actually complete at exactly 4 digits — anything
// longer must be treated the same as the padding-artifact case, not committed as a real value.
function isPaddedYearArtifact(v: string): boolean {
  const yearDigits = v.split('-')[0] ?? '';
  if (yearDigits.length < 4) return false;
  return yearDigits.length > 4 || Number(yearDigits) < 2000;
}

export function DateField({
  label,
  value,
  onChange,
  invalidMessage,
  onValidityChange,
  error,
  helperText,
  onBlur,
  onFocus,
  disableIncompleteCheck,
  inputRef: externalInputRef,
  slotProps,
  ...rest
}: DateFieldProps) {
  const [incomplete, setIncomplete] = useState(false);
  const inputElRef = useRef<HTMLInputElement | null>(null);
  // Tracks whether the field's current DOM value === '' represents a value the user actually
  // *committed* as blank (typed/backspaced their way to a fully empty date, which the browser
  // itself judges valid the instant that happens — badInput false right at that transition, per
  // live testing) as opposed to merely *appearing* empty because some segments are still filled
  // and others aren't (value === '' either way — a native date input's `value` getter can't tell
  // these apart, and even badInput gets unreliably stuck true if re-checked later on the
  // genuinely-blank case). Starts (and resets on each focus) at false, not `value === ''` — a
  // field that's blank because it was simply never touched doesn't need this override at all
  // (badInput is naturally false there too), and defaulting to true would wrongly excuse a
  // partially-typed-then-abandoned date on a field that started blank, since 'input' often never
  // fires while a date stays incomplete (see the listener comment below), leaving this ref stuck
  // at its initial value with no live commit to correct it. Only set true by an actual observed
  // commit in commit() below.
  const legitimatelyBlankRef = useRef(false);
  const showIncomplete = !disableIncompleteCheck && incomplete;
  // A native <input type="date"> only exposes a clear affordance in Chromium (hover-triggered
  // "x"), not Firefox/Safari, and even there a single Backspace/Delete only clears whichever
  // segment currently has focus rather than the whole value — a user has to know to do that
  // three times (month/day/year) to reach blank. An explicit clear button makes "remove the
  // date entirely" a one-click action on every browser, for every optional (non-required) date.
  const showClear = !rest.required && !rest.disabled && value.length > 0;

  function setInputRefs(el: HTMLInputElement | null) {
    inputElRef.current = el;
    if (typeof externalInputRef === 'function') externalInputRef(el);
    else if (externalInputRef) (externalInputRef as { current: HTMLInputElement | null }).current = el;
  }

  function commit(v: string, badInput: boolean): boolean {
    const treatAsIncomplete = badInput || isPaddedYearArtifact(v);
    legitimatelyBlankRef.current = !treatAsIncomplete && v === '';
    if (!disableIncompleteCheck) setIncomplete(treatAsIncomplete);
    if (!treatAsIncomplete) onChange(v);
    return treatAsIncomplete;
  }

  function handleClear() {
    const el = inputElRef.current;
    if (el) el.value = '';
    legitimatelyBlankRef.current = true;
    if (!disableIncompleteCheck) setIncomplete(false);
    onChange('');
  }

  // Read via a ref inside the stable listener below, so that listener can stay attached for the
  // component's whole lifetime instead of being torn down and re-attached on every render (which
  // happens on every keystroke here, since onChange is typically a fresh inline closure from the
  // caller each render) — re-attaching that often risks missing an event in the gap between
  // removeEventListener and addEventListener.
  const commitRef = useRef(commit);
  commitRef.current = commit;

  useEffect(() => {
    const el = inputElRef.current;
    if (el && el.value !== value) el.value = value;
  }, [value]);

  useEffect(() => {
    if (disableIncompleteCheck) return;
    onValidityChange?.(!incomplete);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [incomplete, disableIncompleteCheck]);

  useEffect(() => {
    const el = inputElRef.current;
    if (!el) return;
    function handleInput() {
      commitRef.current(el!.value, el!.validity.badInput);
    }
    function handleKeyUp(e: KeyboardEvent) {
      const isClearingKey = e.key === 'Backspace' || e.key === 'Delete';
      if (isClearingKey && el!.value === '') {
        commitRef.current('', el!.validity.badInput);
      } else if (!isClearingKey) {
        legitimatelyBlankRef.current = false;
      }
    }
    el.addEventListener('input', handleInput);
    el.addEventListener('keyup', handleKeyUp);
    el.addEventListener('change', handleInput);
    return () => {
      el.removeEventListener('input', handleInput);
      el.removeEventListener('keyup', handleKeyUp);
      el.removeEventListener('change', handleInput);
    };
  }, []);

  return (
    <TextField
      {...rest}
      inputRef={setInputRefs}
      label={label}
      type="date"
      defaultValue={value}
      // Browsers cap a bare <input type="date"> at the year 275760 (6 digits) by default —
      // bounding `max` here is what actually stops the native year segment from accepting a
      // 5th/6th keystroke, since isPaddedYearArtifact above can only reject an already-typed
      // value, not prevent the browser's own segment editor from displaying it. A caller-passed
      // slotProps.htmlInput.max (e.g. AuditLogPage's "no future dates" bound) still wins.
      slotProps={{
        ...slotProps,
        htmlInput: { max: '9999-12-31', ...slotProps?.htmlInput },
        input: {
          ...slotProps?.input,
          // A native <input type="date">'s calendar-picker icon is drawn by the browser inside
          // the input's own box, flush against its right edge, as part of the input element
          // itself. Landing the clear button between the date text and that icon would require
          // shrinking the native input's own box via padding — but that box is a browser-native
          // replaced element with no responsive text handling, so once it's forced narrower than
          // its comfortable width the browser clips characters off the displayed date (verified:
          // this happened at every padding value tried, in every narrow context — planner cells,
          // the Issue dialog's Start Date — regardless of how the button itself was positioned).
          // A plain (non-absolutely-positioned) endAdornment costs nothing from the input's own
          // width — it renders as an ordinary flex sibling after the whole input box, landing
          // after the calendar icon instead of before it. That's the deliberate trade-off here:
          // the button sits after the icon rather than before it, but the date text can never be
          // clipped, in any context.
          endAdornment: showClear ? (
            <IconButton
              aria-label="Clear date"
              size="small"
              tabIndex={-1}
              onMouseDown={(e) => e.preventDefault()}
              onClick={handleClear}
            >
              <ClearIcon fontSize="small" />
            </IconButton>
          ) : undefined,
        },
      }}
      onFocus={(e) => {
        legitimatelyBlankRef.current = false;
        onFocus?.(e);
      }}
      onBlur={(e) => {
        const el = e.target;
        const v = el.value;
        // Only trust v === '' as "complete" when legitimatelyBlankRef backs it up — i.e. the
        // blank was actually committed via a real keystroke (see commit()/handleKeyUp above), not
        // merely how an unparseable, still-incomplete date reads back as '' too (same ambiguity
        // described on the ref itself). Trusting any blank-at-blur unconditionally silently
        // resolves an abandoned partial date (e.g. month typed, day/year left untouched) as a
        // legitimate empty value — unnoticed on a required field, since a blank required field
        // already disables Save/Add on its own, but on an optional field it cleared the error and
        // left Save enabled with the partial input silently discarded.
        const badInput = v === '' && legitimatelyBlankRef.current ? false : el.validity.badInput;
        commit(v, badInput);
        if (isPaddedYearArtifact(v)) el.value = value;
        onBlur?.(e);
      }}
      error={showIncomplete || Boolean(error)}
      helperText={showIncomplete ? (invalidMessage ?? 'Please enter a valid date.') : helperText}
      InputLabelProps={{ shrink: true }}
    />
  );
}
